// services/notifications/notificationService.ts (or wherever it is)
import { useCallback, useEffect } from 'react'

import toast from 'react-hot-toast'
import { useQueryClient } from 'react-query'

import { useUserContext } from 'context/userContext'
import NotificationCard from 'features/NotificationCard/NotificationCard'
import { debounce } from 'helpers/hooks'
import { ensurePusher } from 'services/pusherclient/pusherClient'
import { useUserProfile } from 'store'
import { useInboxCacheStore } from 'store/inboxCacheStore'
import { usePendingNotificationsStore } from 'store/pendingNotificationsStore'
import { useProfileFeedCacheStore } from 'store/profileFeedCacheStore'

// In-memory de-dupe
const seenIds = new Set<string>()




const patchVideoThumbnailInQueryData = (
  value: any,
  postId: string,
  videoId: string,
  changes: {
    active_thumb?: string | null
    thumbnail_generating?: boolean
  }
): any => {
  if (value == null) {
    return value
  }

  if (Array.isArray(value)) {
    let changed = false

    const next = value.map(item => {
      const patched = patchVideoThumbnailInQueryData(
        item,
        postId,
        videoId,
        changes
      )

      if (patched !== item) {
        changed = true
      }

      return patched
    })

    return changed ? next : value
  }

  if (typeof value !== 'object') {
    return value
  }

  /*
   * Found the matching post.
   */
  if (
    value.id === postId &&
    Array.isArray(value.videos)
  ) {

    const videoIndex = value.videos.findIndex(
      (video: any) =>
        video?.id === videoId
    )

    if (videoIndex === -1) {
      return value
    }

    const currentVideo = value.videos[videoIndex]

    const nextVideo = {
      ...currentVideo,

      ...(Object.prototype.hasOwnProperty.call(
        changes,
        'active_thumb'
      )
        ? {
            active_thumb:
              changes.active_thumb ?? null,
          }
        : {}),

      ...(Object.prototype.hasOwnProperty.call(
        changes,
        'thumbnail_generating'
      )
        ? {
            thumbnail_generating: Boolean(
              changes.thumbnail_generating
            ),
          }
        : {}),
    }

    const thumbChanged =
      currentVideo.active_thumb !==
      nextVideo.active_thumb

    const generatingChanged =
      Boolean(currentVideo.thumbnail_generating) !==
      Boolean(nextVideo.thumbnail_generating)

    if (!thumbChanged && !generatingChanged) {
      return value
    }

    const nextVideos = [...value.videos]

    nextVideos[videoIndex] = nextVideo

    return {
      ...value,
      videos: nextVideos,
    }
  }

  /*
   * Continue through wrappers such as:
   * data, pages, pagination responses, etc.
   */
  let changed = false
  const nextObject: Record<string, any> = {}

  for (const [key, child] of Object.entries(value)) {
    const patched = patchVideoThumbnailInQueryData(
      child,
      postId,
      videoId,
      changes
    )

    nextObject[key] = patched

    if (patched !== child) {
      changed = true
    }
  }

  return changed ? nextObject : value
}


export const useNotifications = (token: string | null) => {
  const { setNotificationsChannel } = useUserContext()
  const user = useUserProfile()
  const queryClient = useQueryClient()

  const invalidateNotifications = useCallback(() => {
    debounce(() => {
      queryClient.invalidateQueries('notifications')
    }, 300)()
  }, [queryClient])

  useEffect(() => {
    if (!token || !user?.id) return

    const pusher = ensurePusher(token)
    if (!pusher) return

    const channelName = `private-notification.${user.id}`
    const channel = pusher.subscribe(channelName)
    setNotificationsChannel(channel)

const onAny = (eventName: string, data: any) => {
  console.log(
    '%c[NOTIFICATION CHANNEL EVENT]',
    'font-weight:bold; color:#00aaff;',
    {
      channel: channelName,
      eventName,
      data,
      notificationType: data?.notification_type,
      notificationId: data?.notification?.id,
      timestamp: new Date().toISOString(),
    }
  )

  if (eventName?.startsWith('pusher:')) {
    console.log('[NOTIFICATION PUSHER INTERNAL]', eventName, data)
    return
  }

  const normalizedEventName = String(eventName)
    .replace(/^\./, '')
    .toLowerCase()

  const notificationType = String(
    data?.notification_type ?? ''
  ).toLowerCase()

const isVideoThumbnailUpdate =
  normalizedEventName === 'video-thumbnail-updated' ||
  notificationType === 'video-thumbnail-updated'

if (isVideoThumbnailUpdate) {
  const postId = data?.post_id
  const videoId = data?.video_id

  if (
    typeof postId !== 'string' ||
    postId.trim() === '' ||
    typeof videoId !== 'string' ||
    videoId.trim() === ''
  ) {
    return
  }

  const changes: {
    active_thumb?: string | null
    thumbnail_generating?: boolean
  } = {
    thumbnail_generating: Boolean(data?.thumbnail_generating),
  }

  /*
   * On success, replace the thumbnail.
   *
   * On failure, active_thumb will usually be null, so preserve
   * the existing thumbnail rather than clearing it.
   */
  if (
    data?.success !== false &&
    typeof data?.active_thumb === 'string' &&
    data.active_thumb !== ''
  ) {
    changes.active_thumb = data.active_thumb
  }

  /*
   * Update persisted Zustand profile feed caches.
   */
  useProfileFeedCacheStore
    .getState()
    .updateVideoThumbnailState(postId, videoId, changes)

  /*
   * Update any live React Query caches.
   */
  queryClient.setQueriesData(
    {
      predicate: query => {
        const key = query.queryKey

        if (!Array.isArray(key)) {
          return false
        }

        return key.some(part =>
          [
            'getFeed',
            'getSinglePost',
            'getMediaData',
            'profileFeed',
            'getProfileFeed',
          ].includes(String(part))
        )
      },
    },
    cached =>
      patchVideoThumbnailInQueryData(
        cached,
        postId,
        videoId,
        changes
      )
  )

  if (data?.success === false) {
    toast.error('The custom thumbnail could not be generated.')
  }

  return
}


      // robust de-dupe
      const dedupeIdRaw =
       data?.message_id ?? data?.message?.id ?? data?.notification?.id ?? data?.id
      const dedupeId = dedupeIdRaw != null ? String(dedupeIdRaw) : ''
      if (dedupeId) {
        if (seenIds.has(dedupeId)) return
        seenIds.add(dedupeId)
        if (seenIds.size > 5000) {
          const first = seenIds.values().next().value as string | undefined
          if (first) seenIds.delete(first)
        }
      }

      const type = String(
        data?.notification_type ?? eventName
      ).toLowerCase()

      const isMessageMediaFinished =
        normalizedEventName === 'message-media-finished'

      if (isMessageMediaFinished) {
        return
      }

      const isChatPush =
        type !== 'new-message-purchase' &&
        (
          type === 'new-message-notification' ||
          type === 'new-message' ||
          type.includes('message')
        )

      if (type === 'story-finished') {
        queryClient.invalidateQueries(['userStories'])
        return
      }
      const isPhotoProcessingFinished =
        normalizedEventName === 'photo-processing-finished' ||
        notificationType === 'photo-processing-finished'

      if (isPhotoProcessingFinished) {
        if (data?.content_type === 'story') {
          queryClient.invalidateQueries(['userStories'])
        }

        return
      }
      if (isChatPush) {
        //  new payload shape (SentMessageEvent)
        const conversationIdRaw =
          data?.conversation_id ??
          data?.message?.conversation_id ??
          data?.message?.conversation?.id

        const conversationId = conversationIdRaw

        // who sent it? (new payload uses from_user_id)
        const fromUserId = String(
          data?.from_user_id ??
          ''
        )

        //  "don't bump while inside that thread"
        // your routes:
        //   /chat       => inbox page
        //   /chat/:id   => thread page where :id is OTHER USER ID
        const path = window.location.pathname
        const viewingOtherUserId =
          path.startsWith('/chat/')
            ? String(
                path
                  .split('/chat/')[1]
                  ?.split('/')[0] ?? ''
              )
            : ''

        const onThread = viewingOtherUserId !== ''

        const sameThread = onThread && viewingOtherUserId && fromUserId
          ? viewingOtherUserId === fromUserId
          : false
        console.log(sameThread, 'sameThread')

        if (!sameThread) {
          // global badge (top nav etc)
          const s = usePendingNotificationsStore.getState()
          s.setMessages((s.messages || 0) + 1)

          //  per-conversation unread badge (ChatCard uses chat.unread_messages)
          if (conversationId) {
            useInboxCacheStore.getState().bumpUnread(conversationId, 1, {
              setLastMessageBody: data?.body ?? null,
              setLastMessageCreatedAt: data?.created_at ?? null,
            })
          }
        }

        return
      } else {
        const s = usePendingNotificationsStore.getState()
        s.incNotifications(1)
        invalidateNotifications()
      }

      if (!document.hidden && !window.location.href.includes('/chat')) {
        toast.custom(
          <NotificationCard
            data={data}
            type={type}
            time={data?.timestamp ?? ''}
            isToast
          />
        )
      }
    }

    channel.bind_global(onAny)

    return () => {
      try {
        channel.unbind_global(onAny)
        pusher.unsubscribe(channelName)
        // do NOT disconnect here; other features (chat) may be using it
      } catch {}
    }
  }, [token, user?.id, setNotificationsChannel, invalidateNotifications])
}
